import { type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/domain/auth/access';
import { hasEssentialDemoScope } from '@/lib/business/essentialDemoScope';
import { hasStreetSalesScope } from '@/lib/business/streetSalesScope';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: UserRole[];
}

export function ProtectedRoute({ children, allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando sesión…</div>;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/" replace />;
  }

  const restrictedEssentialDemoRoute = hasEssentialDemoScope(user) && (
    location.pathname === '/billing'
    || (location.pathname === '/inventory' && new URLSearchParams(location.search).get('section') === 'purchases')
  );
  if (restrictedEssentialDemoRoute) {
    return <Navigate to={location.pathname === '/inventory' ? '/inventory' : '/dashboard'} replace />;
  }

  if (location.pathname === '/billing' && !user.activeModules.includes('billing')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (location.pathname === '/reports' && !user.activeModules.includes('reports')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (
    location.pathname === '/inventory'
    && new URLSearchParams(location.search).get('section') === 'purchases'
    && !user.activeModules.includes('purchases')
  ) {
    return <Navigate to="/inventory" replace />;
  }

  if (user.isB2B && user.role === 'employee') {
    const employeeRoutes: Record<string, string> = {
      '/orders/capture': 'orders',
      '/orders/history': 'history',
      '/route-customers': 'b2b_customers',
      '/inventory': 'inventory',
      ...(hasStreetSalesScope(user) ? { '/products': 'inventory', '/settings': 'inventory', '/business-sales': 'quotations' } : {}),
    };
    const requiredModule = employeeRoutes[location.pathname];
    const multifoodsCustomerAccess = user.businessId === 'd014a201-ec56-41a5-8ffc-015348a4dcce' && location.pathname === '/route-customers';
    const multifoodsQuoteAccess = user.businessId === 'd014a201-ec56-41a5-8ffc-015348a4dcce'
      && location.pathname === '/business-sales'
      && new URLSearchParams(location.search).get('section') === 'quotes';
    if (!multifoodsCustomerAccess && !multifoodsQuoteAccess && (!requiredModule || !user.activeModules.includes(requiredModule))) {
      const fallback = user.activeModules.includes('orders') ? '/orders/capture' : '/orders/history';
      return <Navigate to={fallback} replace />;
    }
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'employee' || user.role === 'supervisor') {
      return <Navigate to="/orders/capture" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

export function GuestRoute({ children }: { children: ReactNode }) {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Cargando sesión…</div>;
  }

  if (isAuthenticated && user) {
    if (user.role === 'employee' || user.role === 'supervisor') {
      return <Navigate to="/orders/capture" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
